class AcCartOverlay extends HTMLElement {
  #events;
  #read;
  #readPending;
  #readLifecycle = 0;
  #recommendationRead;
  #generation = 0;
  #recommendationGeneration = 0;
  #cart;
  #cartRevision = -1;
  #openReceipts = new WeakMap();
  #openingReceipt = null;
  #quantityCleanup;
  #tasks = Promise.resolve();
  #failures = new Map();
  #retry = new WeakMap();
  #recommendationCache = new Map();
  #codes = [];
  #noteDirty = false;
  #notePending;
  #noteController;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  #removals = new Set();
  #shippingAnimation;
  #reduced = () => this.#motion.matches || document.body.dataset.acMotion === 'reduced';
  #finishRemovals = () => this.#removals.forEach((animation) => animation.finish());
  #syncMotion = () => { if (this.#reduced()) { this.#finishRemovals(); this.#shippingAnimation?.finish(); } };

  connectedCallback() {
    this.#events?.abort();
    this.#events = new AbortController();
    const signal = this.#events.signal;
    const motionObserver = new MutationObserver(this.#syncMotion);
    motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
    this.#motion.addEventListener('change', this.#syncMotion, { signal });
    signal.addEventListener('abort', () => { motionObserver.disconnect(); this.#finishRemovals(); }, { once: true });
    this.addEventListener('click', this.#click, { signal });
    this.addEventListener('submit', this.#submit, { signal });
    this.addEventListener('toggle', (event) => {
      const details = event.target;
      if (!details.open) return;
      if (details.matches('[data-ac-cart-option-editor]')) this.#loadOptions(details);
    }, { signal, capture: true });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-ac-cart-variant-option]')) this.#updateOptions(event.target.closest('form'));
      if (event.target.matches('[name="note"]')) this.#saveNote();
    }, { signal });
    this.addEventListener('input', (event) => {
      if (event.target.matches('[name="note"]')) {
        this.#noteDirty = event.target.value !== event.target.defaultValue;
        this.querySelector('[data-ac-cart-note-status]').textContent = '';
        this.querySelector('[data-ac-cart-note-status]').setAttribute('role', 'status');
      }
    }, { signal });
    document.addEventListener('ac:overlay-open', (event) => {
      if (event.detail?.name !== 'cart') return;
      this.querySelector('[data-ac-cart-scroll]').scrollTop = 0;
      this.#renderShipping(true);
      const receipt = this.#openingReceipt;
      this.#openingReceipt = null;
      if (receipt && receipt.cart === this.#cart && receipt.revision === this.#cartRevision
        && receipt.revision === window.AcCartActions?.revision && !window.AcCartActions?.pending
        && receipt.generation === this.#generation && receipt.lifecycle === this.#readLifecycle
        && receipt.signal === this.#events.signal && !receipt.signal.aborted && this.isConnected) {
        this.#loadRecommendations();
      } else this.refresh();
    }, { signal });
    document.addEventListener('ac:overlay-close', (event) => {
      if (event.detail?.name !== 'cart') return;
      this.#cancelReads();
      if (this.#noteDirty) this.#saveNote();
      this.#finishRemovals();
      this.querySelector('[data-ac-cart-recommendations-content]')?.replaceChildren();
    }, { signal });
    document.addEventListener('ac:cart-updated', (event) => {
      if (!event.detail?.refreshed) this.refresh();
    }, { signal });
    document.addEventListener('ac:cart-write-state', () => {
      this.#generation += 1;
      this.#read?.abort();
    }, { signal });
    window.addEventListener('pageshow', (event) => {
      // History can restore old Liquid HTML even when the document is not in bfcache.
      if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') this.refresh();
    }, { signal });
    const initial = this.querySelector('[data-ac-cart-initial]');
    if (initial) { this.#render(initial.content); initial.remove(); }
  }

  disconnectedCallback() {
    this.#noteController?.abort();
    this.#events?.abort();
    this.#quantityCleanup?.();
    this.#cancelReads();
    window.AcLottieManager?.destroyWithin(this);
  }

  #cancelReads() {
    this.#shippingAnimation?.cancel();
    this.#shippingAnimation = null;
    this.#readLifecycle += 1;
    this.#generation += 1;
    this.#recommendationGeneration += 1;
    this.#read?.abort();
    this.#recommendationRead?.abort();
  }

  createOpenReceipt(cart) {
    if (!cart || cart !== this.#cart || !this.isConnected || !this.#events || this.#events.signal.aborted
      || window.AcCartActions?.pending || this.#cartRevision !== window.AcCartActions?.revision) return null;
    const token = {};
    this.#openReceipts.set(token, { cart, revision: this.#cartRevision, generation: this.#generation,
      lifecycle: this.#readLifecycle, signal: this.#events.signal });
    return token;
  }

  openWithReceipt(token, trigger) {
    this.#openingReceipt = token && this.#openReceipts.get(token);
    if (token) this.#openReceipts.delete(token);
    try { trigger.click(); }
    finally { this.#openingReceipt = null; }
  }

  async refresh(focusTarget) {
    const lifecycle = this.#readLifecycle;
    const viewSignal = this.#events?.signal;
    await window.AcCartActions?.settled();
    if (lifecycle !== this.#readLifecycle || viewSignal?.aborted || !this.isConnected) return null;
    if (!focusTarget && this.#readPending && !this.#read.signal.aborted) return this.#readPending;
    this.#read?.abort();
    const controller = new AbortController();
    this.#read = controller;
    const generation = ++this.#generation;
    const request = this.#fetchState(controller, generation, focusTarget);
    this.#readPending = request;
    try { return await request; }
    finally { if (this.#readPending === request) this.#readPending = null; }
  }

  async #fetchState(controller, generation, focusTarget) {
    try {
      const url = new URL(this.dataset.cartUrl, location.origin);
      url.searchParams.set('section_id', 'ac-cart-drawer-state');
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' }, cache: 'no-store' });
      if (!response.ok) throw new Error(this.dataset.errorLabel);
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (generation !== this.#generation || !this.isConnected) return null;
      this.#render(document, null, focusTarget);
      return this.#cart;
    } catch (error) {
      if (error.name !== 'AbortError') this.#error(error, this.querySelector('[data-ac-cart-error]'), () => this.refresh());
      return null;
    }
  }

  #render(root, payload, focusTarget) {
    const state = root.querySelector('[data-ac-cart-state]');
    const items = state?.querySelector('[data-ac-cart-items]');
    const summary = state?.querySelector('[data-ac-cart-summary]');
    const json = state?.querySelector('[data-ac-cart-json]');
    if (!items || !summary || !json) throw new Error(this.dataset.errorLabel);
    const cart = payload || JSON.parse(json.textContent);
    const scroll = this.querySelector('[data-ac-cart-scroll]');
    const top = scroll.scrollTop;
    const active = focusTarget || document.activeElement;
    const oldItems = this.querySelector('[data-ac-cart-items]');
    const oldRows = [...oldItems.querySelectorAll('[data-ac-cart-line]')];
    const activeRow = active?.closest('[data-ac-cart-line]');
    const rowIndex = oldRows.indexOf(activeRow);
    const controlSelector = ['[data-ac-product-quantity]', '[data-ac-quantity-minus]', '[data-ac-quantity-plus]', '[data-ac-cart-remove]', '[data-ac-wishlist-toggle]', '[data-ac-cart-option-editor] summary'].find(s => active?.matches(s));
    this.#quantityCleanup?.();
    window.AcLottieManager?.destroyWithin(oldItems);
    oldItems.replaceChildren(...items.childNodes);
    this.querySelector('[data-ac-cart-summary]').replaceChildren(...summary.childNodes);
    this.#cart = cart;
    this.#cartRevision = window.AcCartActions?.revision ?? -1;
    const empty = !cart.item_count;
    this.toggleAttribute('data-empty', empty);
    this.querySelector('[data-ac-cart-footer]').hidden = empty;
    this.querySelector('[data-ac-cart-extras]').hidden = empty;
    document.querySelectorAll('[data-ac-ref-cart-count]').forEach(el => { el.textContent = String(cart.item_count || 0); });
    const controls = [...this.querySelectorAll('[data-ac-product-quantity-control]')];
    this.#quantityCleanup = window.AcProductQuantity?.bind(controls, (value, input) => {
      const row = input.closest('[data-ac-cart-line]');
      const quantity = window.AcProductQuantity.normalize(value, {
        min: input.min, max: input.max || null, increment: input.step,
      });
      input.value = input.defaultValue;
      this.#change(row, quantity);
    });
    for (const control of controls) {
      const input = control.querySelector('input');
      control.querySelector('[data-ac-quantity-minus]').disabled = Number(input.value) <= Number(input.min);
      control.querySelector('[data-ac-quantity-plus]').disabled = Boolean(input.max) && Number(input.value) >= Number(input.max);
    }
    if (rowIndex >= 0 && this.contains(active) === false) {
      const rows = [...oldItems.querySelectorAll('[data-ac-cart-line]')];
      const row = rows.find(el => el.dataset.acCartLine === activeRow.dataset.acCartLine) || rows[Math.min(rowIndex, rows.length - 1)];
      (row?.querySelector(controlSelector || 'a') || this.querySelector('[data-ac-overlay-close]'))?.focus({ preventScroll: true });
    }
    const note = this.querySelector('[name="note"]');
    this.#readCodes(cart);
    this.#renderCodes();
    this.#renderShipping();
    const noteIndicator = this.querySelector('[data-ac-cart-note-indicator]');
    if (noteIndicator) noteIndicator.hidden = !note?.defaultValue;
    const failure = this.#failures.values().next().value;
    const errorBox = this.querySelector('[data-ac-cart-error]');
    if (failure) this.#error(failure.error, errorBox, failure.retry);
    else errorBox.hidden = true;
    window.AcLottieManager?.refresh(oldItems);
    scroll.scrollTop = top;
    this.#loadRecommendations();
  }

  #renderShipping(opening = false) {
    const region = this.querySelector('[data-ac-cart-shipping]');
    if (!region || !this.#cart) return;
    const currency = this.#cart.currency;
    const rate = currency === this.dataset.baseCurrency ? 1
      : window.Shopify?.currency?.active === currency ? Number(window.Shopify.currency.rate) : NaN;
    const threshold = Number(this.dataset.shippingThreshold) * 100 * rate;
    region.hidden = !this.#cart.item_count || this.#cart.requires_shipping === false || !(threshold > 0);
    if (region.hidden) { this.#shippingAnimation?.cancel(); this.#shippingAnimation = null; return; }
    const remaining = Math.max(0, threshold - this.#cart.total_price);
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency });
    const unit = 10 ** formatter.resolvedOptions().maximumFractionDigits;
    const amount = formatter.format(Math.ceil(remaining / 100 * unit) / unit);
    const progress = Math.min(100, Math.max(0, this.#cart.total_price / threshold * 100));
    const template = region.querySelector(remaining > 0 ? '[data-ac-cart-shipping-remaining]' : '[data-ac-cart-shipping-ready]');
    const content = template.content.cloneNode(true);
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) walker.currentNode.textContent = walker.currentNode.textContent.replaceAll('[amount]', amount);
    const message = region.querySelector('[data-ac-cart-shipping-message]');
    if (message.textContent !== content.textContent) message.replaceChildren(content);
    region.toggleAttribute('data-complete', remaining === 0);
    const track = region.querySelector('[data-ac-cart-shipping-progress]');
    track.setAttribute('aria-valuenow', String(Math.round(progress)));
    track.setAttribute('aria-valuetext', message.textContent);
    const fill = region.querySelector('[data-ac-cart-shipping-fill]');
    const target = `scaleX(${progress / 100})`;
    if (!opening && fill.style.transform === target) return;
    const from = opening ? 'scaleX(0)' : getComputedStyle(fill).transform;
    this.#shippingAnimation?.cancel();
    fill.style.transform = target;
    if (!this.#reduced() && ['open','opening'].includes(this.dataset.state)) {
      this.#shippingAnimation = fill.animate([{transform: from}, {transform: target}], {duration: opening ? 650 : 320, easing: 'cubic-bezier(.2,.7,.2,1)'});
      this.#shippingAnimation.finished.catch(() => {});
    }
  }

  async #loadOptions(details) {
    const host = details.querySelector('[data-ac-cart-option-content]');
    if (host.dataset.loaded || host.getAttribute('aria-busy')) return;
    host.setAttribute('aria-busy', 'true');
    host.textContent = this.dataset.loadingLabel;
    try {
      const url = new URL(details.dataset.productUrl, location.origin);
      url.searchParams.set('section_id', 'ac-cart-variant-editor');
      url.searchParams.set('variant', details.dataset.variantId);
      const response = await fetch(url, { signal: this.#events.signal });
      if (!response.ok) throw new Error(this.dataset.optionErrorLabel);
      const root = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = root.querySelector('.shopify-section');
      if (!content?.children.length) throw new Error(this.dataset.optionErrorLabel);
      if (!details.isConnected) return;
      host.replaceChildren(...content.childNodes);
      host.dataset.loaded = 'true';
      this.#updateOptions(host.querySelector('form'));
    } catch (error) {
      if (!details.isConnected || error.name === 'AbortError') return;
      const link = document.createElement('a');
      link.href = details.dataset.productUrl;
      link.className = 'ac-text-link ac-type-body-prompt';
      link.textContent = this.dataset.optionErrorLabel;
      host.replaceChildren(link);
    } finally { host.removeAttribute('aria-busy'); }
  }

  #updateOptions(form) {
    if (!form) return;
    const variants = JSON.parse(form.querySelector('[data-ac-cart-variants]').textContent);
    const fields = [...form.querySelectorAll('select')];
    // Later choices follow the preceding choices, so changing color can select an available size.
    fields.forEach((field, index) => {
      for (const option of field.options) {
        option.disabled = !variants.some(variant => variant.available && variant.options[index] === option.value
          && fields.slice(0, index).every((prior, position) => variant.options[position] === prior.value));
      }
      if (field.selectedOptions[0]?.disabled) field.value = [...field.options].find(option => !option.disabled)?.value || '';
    });
    const variant = variants.find(item => item.available && fields.every((field, index) => item.options[index] === field.value));
    form.dataset.variant = variant?.id || '';
    form.querySelector('[data-ac-cart-variant-save]').disabled = !variant
      || String(variant.id) === form.closest('[data-ac-cart-option-editor]').dataset.variantId;
  }

  #saveNote() {
    const editor = this.querySelector('[data-ac-cart-note-editor]');
    const input = editor?.querySelector('textarea');
    if (!input || !this.#noteDirty || input.readOnly) return Promise.resolve(true);
    if (this.#notePending) return this.#notePending;
    const note = input.value;
    const button = editor.querySelector('[data-ac-cart-save-note]');
    const status = editor.querySelector('[data-ac-cart-note-status]');
    const controller = new AbortController();
    this.#noteController = controller;
    const timeout = setTimeout(() => controller.abort(new Error(this.dataset.errorLabel)), 10000);
    button.disabled = true;
    status.textContent = '';
    status.setAttribute('role', 'status');
    this.#notePending = (async () => {
      try {
        const cart = await window.AcCartActions.saveNote(note, controller.signal);
        if ((cart.note || '') !== note) throw new Error(this.dataset.errorLabel);
        // Native defaultValue is the last acknowledged note; cart renders must not replace a newer draft.
        input.defaultValue = note;
        this.#noteDirty = input.value !== note;
        status.textContent = this.#noteDirty ? '' : this.dataset.savedLabel;
        this.querySelector('[data-ac-cart-note-indicator]').hidden = !note;
        return true;
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          status.textContent = error.name === 'TypeError' ? this.dataset.errorLabel : error.message || this.dataset.errorLabel;
          status.setAttribute('role', 'alert');
        }
        return false;
      } finally {
        clearTimeout(timeout);
        button.disabled = false;
        this.#noteController = null;
        this.#notePending = null;
      }
    })();
    return this.#notePending;
  }

  #readCodes(cart) {
    if (Array.isArray(cart.discount_codes)) {
      this.#codes = cart.discount_codes.map(entry => ({ code: entry.code, applicable: entry.applicable }));
      return;
    }
    const allocations = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(entry => entry.discount_application)),
    ];
    const applied = allocations.filter(entry => entry.type === 'discount_code').map(entry => entry.title);
    const codes = [...new Set([...this.#codes.map(entry => entry.code), ...applied])];
    this.#codes = codes.map(code => ({ code, applicable: applied.some(title => title.toLowerCase() === code.toLowerCase()) ? true : undefined }));
  }

  #renderCodes() {
    const list = this.querySelector('[data-ac-cart-codes]');
    if (!list) return;
    const indicator = this.querySelector('[data-ac-cart-code-count]');
    if (indicator) {
      indicator.hidden = !this.#codes.length;
      indicator.textContent = this.#codes.length ? String(this.#codes.length) : '';
    }
    list.replaceChildren(...this.#codes.map(({code, applicable}) => {
      const row = document.createElement('li');
      const label = document.createElement('span');
      label.textContent = code + ' — ' + (applicable === true ? this.dataset.codeAppliedLabel : applicable === false ? this.dataset.codeInapplicableLabel : this.dataset.codePendingLabel);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ac-button ac-button--text ac-action--marker ac-text-link--underline';
      button.dataset.acCartRemoveCode = code;
      button.setAttribute('aria-label', this.dataset.removeCodeLabel + ': ' + code);
      const text = document.createElement('span');
      text.className = 'ac-action__label';
      text.textContent = this.dataset.removeCodeLabel;
      button.append(text);
      row.append(label, button);
      return row;
    }));
  }

  #error(error, container, retry) {
    if (!container) return;
    container.hidden = false;
    container.querySelector('p').textContent = error.message || this.dataset.errorLabel;
    this.#retry.set(container, retry);
    window.AcCartActions?.announce(error.message || this.dataset.errorLabel);
  }

  #mutate(key, operation, { row, after, remove = false } = {}) {
    const viewSignal = this.#events.signal;
    const run = async () => {
      row = row?.isConnected ? row : [...this.querySelectorAll('[data-ac-cart-line]')].find(el => el.dataset.acCartLine === row?.dataset.acCartLine);
      const errorBox = row?.querySelector('[data-ac-cart-line-error]') || this.querySelector('[data-ac-cart-error]');
      const focused = document.activeElement;
      let written = false;
      row?.setAttribute('aria-busy', 'true');
      const controls = [...(row || this).querySelectorAll(row ? 'button,input,select' : '[data-ac-cart-discount-form] button')];
      const disabled = controls.map(control => control.disabled);
      controls.forEach(control => { control.disabled = true; });
      try {
        const payload = await operation();
        written = true;
        const revision = window.AcCartActions.revision;
        const pending = window.AcCartActions.pending;
        await window.AcCartActions.settled();
        if (viewSignal.aborted) return;
        if (row && remove && row.isConnected && !this.#reduced()) {
          const animation = row.animate([{opacity:1, maxHeight:row.offsetHeight+'px'}, {opacity:0, maxHeight:'0px', paddingBlock:'0px'}], {duration:220,easing:'ease',fill:'forwards'});
          this.#removals.add(animation);
          try { await animation.finished; } catch { /* The successful cart write must still be rendered. */ }
          finally { this.#removals.delete(animation); }
        }
        if (viewSignal.aborted) return;
        const html = payload.sections?.['ac-cart-drawer-state'];
        const focusTarget = document.activeElement === document.body ? focused : null;
        if (html && !pending && revision === window.AcCartActions.revision) this.#render(new DOMParser().parseFromString(html, 'text/html'), payload, focusTarget);
        else if (!await this.refresh(focusTarget)) throw new Error(this.dataset.errorLabel);
        after?.(payload);
        this.#failures.delete(key);
        errorBox.hidden = true;
        if (!this.#failures.size) this.querySelector('[data-ac-cart-error]').hidden = true;
        document.dispatchEvent(new CustomEvent('ac:cart-updated', {detail:{cart:this.#cart,refreshed:true}}));
      } catch (error) {
        if (viewSignal.aborted) return;
        // A successful write must never be repeated just because its HTML refresh failed.
        const retry = written ? async () => {
          if (await this.refresh()) {
            after?.(this.#cart);
            this.#failures.delete(key);
            if (!this.#failures.size) this.querySelector('[data-ac-cart-error]').hidden = true;
          }
        } : () => this.#mutate(key, operation, {row,after,remove});
        if (error.cartRestored) this.#failures.delete(key);
        else this.#failures.set(key, {error,retry});
        this.#error(error, errorBox.isConnected ? errorBox : this.querySelector('[data-ac-cart-error]'), retry);
      } finally {
        row?.removeAttribute('aria-busy');
        controls.forEach((control,index) => { if (control.isConnected) control.disabled = disabled[index]; });
      }
    };
    this.#tasks = this.#tasks.then(run, run);
    return this.#tasks;
  }

  #change(row, quantity) {
    if (!row || row.hasAttribute('aria-busy')) return;
    const key = row.dataset.acCartLine;
    const original = this.#cart.items.find(item => item.key === key);
    const identity = item => JSON.stringify([item.variant_id, item.properties || {}, item.selling_plan_allocation?.selling_plan?.id || null]);
    return this.#mutate('line:' + key, () => {
      // Shopify can change line keys when discounts change. Resolve only an unambiguous match.
      const exact = this.#cart.items.find(item => item.key === key);
      const matches = original ? this.#cart.items.filter(item => identity(item) === identity(original)) : [];
      const current = exact || (matches.length === 1 ? matches[0] : null);
      if (!current) throw new Error(this.dataset.errorLabel);
      return window.AcCartActions.change(current.key, quantity, key);
    }, {row,remove:quantity === 0});
  }

  #click = (event) => {
    if (event.target.closest('[data-ac-cart-save-note]')) { this.#saveNote(); return; }
    const tool = event.target.closest('[data-ac-cart-tool-trigger]');
    if (tool) {
      const open = tool.getAttribute('aria-expanded') !== 'true';
      this.querySelectorAll('[data-ac-cart-tool-trigger]').forEach(button => {
        const expanded = button === tool && open;
        button.setAttribute('aria-expanded', String(expanded));
        this.querySelector('#' + button.getAttribute('aria-controls')).hidden = !expanded;
      });
      return;
    }
    const remove = event.target.closest('[data-ac-cart-remove]');
    if (remove) { event.preventDefault(); this.#change(remove.closest('[data-ac-cart-line]'),0); return; }
    const retry = event.target.closest('[data-ac-cart-retry]');
    if (retry) { this.#retry.get(retry.closest('.ac-cart-error'))?.(); return; }
    const code = event.target.closest('[data-ac-cart-remove-code]');
    if (code) {
      document.dispatchEvent(new CustomEvent('ac:discount-code-removed', {detail:{code:code.dataset.acCartRemoveCode}}));
      this.#mutate('discount', () => window.AcCartActions.update({discount: this.#codes.filter(entry => entry.code !== code.dataset.acCartRemoveCode).map(entry => entry.code).join(',')}),
        {after: () => { this.#codes = this.#codes.filter(entry => entry.code !== code.dataset.acCartRemoveCode); this.#renderCodes(); }});
    }
  };

  #submit = async (event) => {
    const form = event.target;
    if (form.matches('[data-ac-cart-variant-form]')) {
      event.preventDefault();
      const row = form.closest('[data-ac-cart-line]');
      if (row.hasAttribute('aria-busy') || !form.dataset.variant) return;
      const original = this.#cart.items.find(item => item.key === row.dataset.acCartLine);
      const variant = form.dataset.variant;
      this.#mutate('replace:' + original.key, () => window.AcCartActions.replace(original, variant), {row});
    } else if (form.matches('[data-ac-cart-checkout-form]')) {
      event.preventDefault();
      const button = form.querySelector('[name="checkout"]');
      if (button.disabled) return;
      button.disabled = true;
      const note = this.querySelector('[name="note"]');
      if (note) note.readOnly = true;
      this.#noteController?.abort();
      window.AcCartActions.cancelWelcomeDiscount();
      try {
        await this.#tasks;
        await window.AcCartActions.checkoutReady();
        if (this.#failures.size) throw this.#failures.values().next().value.error;
        const field = form.querySelector('input[name="checkout"]') || document.createElement('input');
        field.type = 'hidden'; field.name = 'checkout'; field.value = '1'; form.append(field);
        HTMLFormElement.prototype.submit.call(form);
      } catch (error) {
        this.#error(error, this.querySelector('[data-ac-cart-error]'), () => {
          const failure = this.#failures.values().next().value;
          if (failure) failure.retry();
          else this.refresh();
        });
        button.disabled = false;
        if (note) note.readOnly = false;
      }
    } else if (form.matches('[data-ac-cart-discount-form]')) {
      event.preventDefault();
      const input = form.querySelector('input');
      const code = input.value.trim();
      if (!code || code.includes(',')) return;
      this.#mutate('discount', () => window.AcCartActions.update({discount: [...new Set([...this.#codes.map(entry=>entry.code), code])].join(',')}),
        {after: (cart) => {
          const status = this.querySelector('[data-ac-cart-discount-status]');
          const result = this.#codes.find(entry => entry.code.toLowerCase() === code.toLowerCase());
          if (!result) { this.#codes.push({code,applicable:false}); this.#renderCodes(); }
          status.textContent = result?.applicable === true ? this.dataset.codeAppliedLabel : result?.applicable === undefined && result ? this.dataset.codePendingLabel : this.dataset.codeInapplicableLabel;
          input.value = '';
        }});
    }
  };

  async #loadRecommendations() {
    const region = this.querySelector('[data-ac-cart-recommendations]');
    if (!region) return;
    const seed = this.#cart?.items?.[0]?.product_id;
    const host = region.querySelector('[data-ac-cart-recommendations-content]');
    const open = this.dataset.state === 'open' || this.dataset.state === 'opening';
    const products = [...new Set((this.#cart?.items || []).map(item => String(item.product_id)))].sort().join(',');
    if (open && seed && host.dataset.seed === String(seed) && host.dataset.products === products && host.children.length) return;
    const generation = ++this.#recommendationGeneration;
    this.#recommendationRead?.abort();
    if (!seed) { region.hidden = true; host.replaceChildren(); return; }
    if (!open) return;
    const controller = new AbortController();
    this.#recommendationRead = controller;
    try {
      let html = this.#recommendationCache.get(seed);
      if (!html) {
        const url = new URL(this.dataset.recommendationsUrl, location.origin);
        url.search = new URLSearchParams({section_id:'ac-cart-recommendations',product_id:seed,limit:10,intent:'related'});
        let response;
        try {
          response = await fetch(url, {signal:controller.signal});
          if (!response.ok) throw new Error('Recommendations unavailable');
        } catch (error) {
          if (error.name === 'AbortError' || controller.signal.aborted || generation !== this.#recommendationGeneration) throw error;
          const fallback = new URL(this.dataset.cartUrl, location.origin);
          fallback.searchParams.set('section_id', 'ac-cart-recommendations');
          response = await fetch(fallback, {signal:controller.signal});
          if (!response.ok) throw new Error('Recommendations unavailable');
        }
        html = await response.text();
        this.#recommendationCache.set(seed,html);
        // Bound per-document recommendation HTML; insertion order gives FIFO eviction.
        if (this.#recommendationCache.size > 8) {
          this.#recommendationCache.delete(this.#recommendationCache.keys().next().value);
        }
      }
      if (generation !== this.#recommendationGeneration || !this.isConnected) return;
      const root = new DOMParser().parseFromString(html,'text/html');
      const rail = root.querySelector('ac-product-rail');
      if (!rail) return;
      const exclude = new Set(this.#cart.items.map(item=>String(item.product_id)));
      let count = 0;
      for (const item of rail.querySelectorAll('[data-ac-cart-recommendation-id]')) {
        const id = item.dataset.acCartRecommendationId;
        if (exclude.has(id) || count >= Number(this.dataset.recommendationsCount)) item.remove();
        else { exclude.add(id); count += 1; }
      }
      region.hidden = count === 0;
      const needsSwiper = !window.Swiper;
      host.replaceChildren(rail);
      host.dataset.seed = String(seed);
      host.dataset.products = products;
      // Connected rails already initialize themselves when Swiper is available.
      if (!needsSwiper) return;
      try {
        await window.AcProductGallery.ensureSwiper({script:this.dataset.swiperScript,stylesheet:this.dataset.swiperStylesheet});
        if (generation === this.#recommendationGeneration && rail.isConnected) rail.refresh?.();
      } catch { /* Native overflow remains a usable fallback. */ }
    } catch (error) {
      if (error.name !== 'AbortError' && generation === this.#recommendationGeneration && this.isConnected) region.hidden = true;
    }
  }
}
if (!customElements.get('ac-cart-overlay')) customElements.define('ac-cart-overlay', AcCartOverlay);
